import React from 'react';
import * as Scrivito from 'scrivito';
import Navigation from '../../components/Navigation';
import Footer from '../../components/Footer';
import CookieNotice from '../../components/CookieNotice';
import SeoHead from '../../components/SeoHead';
import { requestInquiryTab } from '../../lib/inquiryTab';
import { currentOrigin, toAbsoluteUrl } from '../../lib/origin';
import SplitFlap from '../../components/SplitFlap';
import { pick } from '../../lib/localize';

// Latin-script renderings of "translation" — the split-flap board's signature
// cycle. Limited to languages the self-hosted latin/latin-ext font subsets
// (see _fonts.scss) actually cover, so every character comes from the same
// typeface instead of silently falling back on a handful of glyphs.
const TRANSLATION_WORDS = [
  'TRANSLATION', 'ÜBERSETZUNG', 'TRADUCTION', 'TRADUCCIÓN', 'TRADUZIONE',
  'TRADUÇÃO', 'VERTALING', 'TŁUMACZENIE', 'ÇEVİRİ', 'TRADUCERE', 'PŘEKLAD',
  'ÖVERSÄTTNING',
];

// Small, deliberately hardcoded copy for the hero eyebrow/ticker: unlike the
// rest of the page these aren't editorial fields on the Homepage obj, just a
// fixed factual strip (see public/llms.txt), so it is localised here instead
// of adding CMS attributes an editor would have to fill in for one sentence.
const HERO_COPY = {
  de: {
    since: 'Seit 1980',
    cert: 'DIN EN ISO 17100',
    ticker: ['5.000+ Projekte', 'bis zu 18 Sprachen pro Projekt', 'bis zu 4.000 Seiten', 'DIN EN ISO 17100 zertifiziert'],
  },
  en: {
    since: 'Since 1980',
    cert: 'DIN EN ISO 17100',
    ticker: ['5,000+ projects delivered', 'up to 18 languages per project', 'up to 4,000 pages', 'DIN EN ISO 17100 certified'],
  },
};

// `country` picks the flag shown in the language dropdown and holds a country
// name, not a language code – the Rails app derived the enum from the flag
// image filenames. Only the flags actually in use are shipped in public/flags,
// so adding a language means adding its PNG there and its name here.
const COUNTRY_VALUES = ['Germany', 'United_Kingdom'];

Scrivito.provideObjClass('Homepage', {
  attributes: {
    title: 'string',
    body: 'widgetlist',
    childOrder: 'referencelist',
    country: ['enum', { values: COUNTRY_VALUES }],
    countryNameTranslation: 'string',
    isoCodeLanguage: 'string',
    metaTags: 'string',
    metaDescription: 'string',
    // Hero
    universal: 'string',
    firstSubHeadline: 'string',
    secondSubHeadline: 'string',
    buttonTextService: 'string',
    buttonTextOffer: 'string',
    buttonTextWorkWith: 'string',
    button_1: 'string',
    button_2: 'string',
    button_3: 'string',
    sliderImage: 'reference',
    menuLanguage: 'string',
    // Mail templates
    mailThanksServicesBeginning: 'html',
    mailThanksServicesEnd: 'html',
    mailThanksForWorkingWithUs: 'html',
    mailThanksForWorkingWithUsEnd: 'html',
    mailerSuccess: 'string',
    // Footer
    contactUsText: 'string',
    transTechTextHeadline: 'string',
    transTechText: 'string',
    rightLinksHeadline: 'string',
    rightsLinks: 'referencelist',
    adress: 'string',
    email: 'string',
    number: 'string',
    copyRightyText: 'string',
    moreSites: 'string',
    cookieNotice: 'string',
    cookieNoticeLink: 'link',
    cookieNoticeButtonText: 'string',
  },
});

// The Rails layout hard-coded one title for every page - and this replaced it
// with one shared literal too, still identical for both the German and
// English Homepage, which is exactly the kind of duplicate <title> an
// SEO/GEO audit flags as two pages competing with each other in search
// instead of being distinguishable variants. Keyed by language so each one
// actually describes the page it belongs to.
const SITE_TITLE_BY_LANG = {
  de: 'trans-tech – Technische Übersetzungen Berlin, seit 1980',
  en: 'trans-tech - technical translations Berlin, since 1980',
};

const HomepageComponent = Scrivito.connect(({ page }) => {
  const sliderImage = page.get('sliderImage');
  const metaDescription = page.get('metaDescription');
  const metaTags = page.get('metaTags');
  const isoCode = page.get('isoCodeLanguage') || 'en';
  const heroCopy = pick(HERO_COPY, isoCode.slice(0, 2));
  const siteTitle = pick(SITE_TITLE_BY_LANG, isoCode.slice(0, 2));

  // .contentUrl() always appends ?nowebp=1 (it's a "download the exact
  // original file" URL) - fine, even desirable, for the og:image use below
  // (guarantees social-media crawlers that don't support WebP still get a
  // preview), but not for the CSS background.
  const heroOgImageUrl = sliderImage ? (sliderImage.contentUrl() || null) : null;
  // The background is instead a CDN-transformed copy (WebP content-negotiated),
  // sized per breakpoint: the untransformed original was 404KB and, as a
  // High-priority request, took bandwidth from the CSS, fonts and JS on a phone
  // (Lighthouse, slow-4G mobile) - all to sit under a ~90%-opaque tint.
  // main.scss's .tt-hero--photo picks one of the two custom properties by
  // media query, so a browser only ever downloads the one it uses.
  const heroBlob = sliderImage && sliderImage.objClass() === 'Image' ? sliderImage.get('blob') : null;
  const heroStyle = heroBlob
    ? {
        '--tt-hero-bg-sm': `url(${heroBlob.optimizeFor({ width: 480 }).url()})`,
        '--tt-hero-bg-lg': `url(${heroBlob.optimizeFor({ width: 1920 }).url()})`,
      }
    : {};

  // Each language has its own Homepage obj (see COUNTRY_VALUES above) – list
  // them as hreflang alternates so search engines connect the variants
  // instead of treating them as unrelated/duplicate content.
  const origin = currentOrigin();
  const alternates = Scrivito.Obj.where('_objClass', 'equals', 'Homepage')
    .toArray()
    .map((hp) => ({ iso: hp.get('isoCodeLanguage'), href: toAbsoluteUrl(Scrivito.urlFor(hp), origin) }))
    .filter((alt) => alt.iso && alt.href);

  const address = page.get('adress');
  const email = page.get('email');
  const phone = page.get('number');

  // Contact details live on the per-language Homepage, not globally, so the
  // Organization schema is declared here rather than once for the whole site.
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'trans-tech',
    url: toAbsoluteUrl(Scrivito.urlFor(page), origin),
    ...(metaDescription && { description: metaDescription }),
    ...(email && { email }),
    ...(phone && { telephone: phone }),
    ...(address && { address: { '@type': 'PostalAddress', streetAddress: address } }),
  };

  return (
    <>
      <SeoHead
        page={page}
        lang={isoCode}
        title={siteTitle}
        description={metaDescription}
        keywords={metaTags}
        ogImage={heroOgImageUrl || '/bg-1.jpg'}
        structuredData={organizationSchema}
      >
        {alternates.map((alt) => (
          <link key={alt.iso} rel="alternate" hrefLang={alt.iso} href={alt.href} />
        ))}
      </SeoHead>

      <Navigation page={page} />
      <CookieNotice page={page} />

      {/* Hero — signature split-flap board over a blueprint/graph-paper scene */}
      <section
        id="home"
        className={`tt-hero full_width_section${heroBlob ? ' tt-hero--photo' : ''}`}
        style={heroStyle}
      >
        <svg className="tt-hero__skyline" viewBox="0 0 600 300" fill="none" aria-hidden="true">
          <g stroke="currentColor" strokeWidth="1.4">
            <line x1="0" y1="280" x2="600" y2="280" />
            <rect x="40" y="200" width="46" height="80" />
            <rect x="96" y="230" width="34" height="50" />
            <rect x="470" y="210" width="50" height="70" />
            <rect x="530" y="240" width="30" height="40" />
            <rect x="150" y="245" width="60" height="35" />
            {/* Fernsehturm — Berlin's TV tower, the one skyline shape that
                reads instantly and reinforces "this is Berlin" without a
                stock photo. */}
            <line x1="330" y1="280" x2="330" y2="60" />
            <circle cx="330" cy="95" r="22" />
            <line x1="330" y1="60" x2="330" y2="20" />
            <line x1="318" y1="280" x2="342" y2="280" />
          </g>
        </svg>

        <div className="container tt-hero__inner">
          <div className="tt-hero__eyebrow">
            <span className="tt-hero__badge">Berlin</span>
            <span className="tt-hero__badge">{heroCopy.since}</span>
            <span className="tt-hero__badge"><strong>{heroCopy.cert}</strong></span>
          </div>

          <Scrivito.ContentTag
            tag="h1"
            attribute="universal"
            content={page}
            className="tt-hero__title"
          />
          <Scrivito.ContentTag
            tag="p"
            attribute="firstSubHeadline"
            content={page}
            className="tt-hero__subtitle"
          />

          <div className="tt-hero__flap-row">
            <SplitFlap words={TRANSLATION_WORDS} size="lg" />
          </div>

          <div className="tt-hero__actions">
            <a
              href={`#${page.get('button_1') || 'our_services'}`}
              className="btn btn-lg btn-dark-tt btn-radius"
            >
              <Scrivito.ContentTag tag="span" attribute="buttonTextService" content={page} />
              <span> <i className="ti-help" /> </span>
            </a>
            <a
              id="offer-trigger"
              href={`#${page.get('button_2') || 'request_information'}`}
              className="btn btn-lg btn-skin btn-radius"
              onClick={() => requestInquiryTab('inquiry')}
            >
              <Scrivito.ContentTag tag="span" attribute="buttonTextOffer" content={page} />
              <span> <i className="ti-settings" /> </span>
            </a>
            <a
              id="work-with-us-trigger"
              href={`#${page.get('button_3') || 'request_information'}`}
              className="btn btn-lg btn-dark-tt btn-radius"
              onClick={() => requestInquiryTab('work')}
            >
              <Scrivito.ContentTag tag="span" attribute="buttonTextWorkWith" content={page} />
              <span> <i className="ti-user" /> </span>
            </a>
          </div>
        </div>

        <div className="tt-ticker">
          <div className="tt-ticker__track">
            {[...heroCopy.ticker, ...heroCopy.ticker].map((line, i) => (
              <span className="tt-ticker__item" key={i}>{line}</span>
            ))}
          </div>
        </div>
      </section>

      <main>
        <Scrivito.ContentTag tag="div" attribute="body" content={page} />
      </main>

      <Footer page={page} />
    </>
  );
});

Scrivito.provideComponent('Homepage', HomepageComponent);

export default HomepageComponent;
